import cloneDeep from 'lodash/cloneDeep';

import { Injectable } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';

import { UpdatePoint } from './points.actions';

import { TWorkspacesById } from '../workspace/workspace.model';
import { TPoint, TPoints, TPointsByWorkspace } from './points.model';

import { ActiveService } from '../../services/active/active.service';
import { ResponseErrorService } from '../errors/response-error.service';
import { WorkspaceService } from '../workspace/workspace.service';
import { PointsGenerateService } from './point-generate/points-generate.service';

import { PointsApiProviderService } from '@core/api';
import { TPointCustomField } from '@project/view-models';
import { catchError, map } from 'rxjs/operators';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { RelatedPointsCacheService } from '../../components/related-points/related-points-cache.service';
import {
  TNewPoint,
  TNewPointAttachments,
} from '../../data-providers/api-providers/points-api-provider/points-requests.model';
import { NEW_POINT_ID } from '../../shared/constants/point.const';
import { EDocumentType } from '../../shared/enums/document-type.enum';
import { EStore } from '../../shared/enums/store.enum';
import { ECustomFieldType } from '../custom-fields/custom-field-types-enums';
import { GET_CUSTOM_FIELDS } from '../custom-fields/custom-fields.store';
import { CustomTableService } from '../site/site-table/custom-table/custom-table.service';
import { SiteTablePointsService } from '../site/site-table/site-table-points.service';
import { AttachmentsService } from './attachments/attachments.service';
import { polygonsToArea } from './point-generate/polygon-to-area';
import { SET_POINTS } from './points.store';
import { getSelectedPoints } from './selected-points';
import { getDescriptionMentions } from './utils/get-description-mention';

@Injectable({
  providedIn: 'root',
})
export class PointsService {
  private points$: Observable<TPointsByWorkspace>;
  private points: TPointsByWorkspace = {};
  private mapPoints: TPoint[] = [];

  constructor(
    private store: Store<{
      points: TPointsByWorkspace;
      workspaces: TWorkspacesById;
    }>,
    private pointsApiProviderService: PointsApiProviderService,
    private responseErrorService: ResponseErrorService,
    private activeService: ActiveService,
    private workspaceService: WorkspaceService,
    private pointsGenerateService: PointsGenerateService,
    private siteTablePointsService: SiteTablePointsService,
    private attachmentsService: AttachmentsService,
    private customTableService: CustomTableService,
    private featureFlagService: FeatureFlagService,
    private relatedPointsCacheService: RelatedPointsCacheService,
  ) {
    this.points$ = this.store.pipe(select(EStore.POINTS));

    this.points$.subscribe((points) => {
      const activeWorkspaceId = this.activeService.getActiveWorkspaceId();
      this.points = cloneDeep(points);

      if (activeWorkspaceId && points[activeWorkspaceId]) {
        SET_POINTS(cloneDeep(points[activeWorkspaceId].entities));
      } else {
        let _points = [];

        Object.keys(points).forEach((workspaceId) => {
          _points = [..._points, ...points[workspaceId].entities];
        });

        SET_POINTS(cloneDeep(_points));
      }
    });
  }

  getAllPoints(): TPointsByWorkspace {
    return this.points;
  }

  getPointsData(workspaceId: string = null): TPoints {
    const pointsWorkspaceId = workspaceId || this.activeService.getActiveWorkspaceId();

    return this.points[pointsWorkspaceId];
  }

  getPoints(workspaceId: string = null): TPoint[] {
    const points = this.getPointsData(workspaceId);
    return points ? points.entities : [];
  }

  getPointsForWorkspace(workspaceId: string): TPoint[] {
    const points = this.points[workspaceId];
    return points ? points.entities : [];
  }

  getSelectedPoints(): TPoint[] {
    const selectedPointsIds = getSelectedPoints();
    const points = this.getPointsData();

    return points ? points.entities.filter((point) => selectedPointsIds.includes(point._id)) : [];
  }

  findPoint(_id: string): TPoint {
    if (this.workspaceService.getActiveWorkspace()) {
      return this.getPoints().find((point) => point._id === _id);
    } else {
      const points = this.getAllPoints();
      let allPoints: TPoint[] = [];

      Object.keys(points).forEach((workspaceId) => {
        allPoints = [...allPoints, ...points[workspaceId].entities];
      });

      return allPoints.find((point) => point._id === _id);
    }
  }

  createPoint(point: TPoint): Observable<TPoint> {
    const newPoint = this.createNewPoint(point);
    const relatedPointsFlagEnabled = this.featureFlagService.getFlag(EFeatureFlag.RELATED_POINTS);
    const create$ = relatedPointsFlagEnabled
      ? this.pointsApiProviderService.createPointWithRelationships(point.workspaceRef.id, {
        ...newPoint,
        relationships: this.relatedPointsCacheService.getPendingRelationships(NEW_POINT_ID),
      })
      : this.pointsApiProviderService.createPoint(point.workspaceRef.id, newPoint);

    return create$.pipe(
      map((response) => {
        const generatedPoint = this.pointsGenerateService.generatePoint(response);

        if (relatedPointsFlagEnabled) {
          this.relatedPointsCacheService.clearPendingRelationships(NEW_POINT_ID);
        }

        this.store.dispatch(
          new UpdatePoint({
            workspaceId: generatedPoint.workspaceRef.id,
            point: generatedPoint,
          }),
        );

        this.customTableService.clearGroupSizes();

        return generatedPoint;
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to create point'),
      ),
    );
  }

  //
  // Points
  //

  createNewPoint(point: TPoint): TNewPoint {
    const polygons = polygonsToArea(point.polygons);

    const {
      customFieldsList,
      mentions,
    }: { customFieldsList: TPointCustomField[]; mentions: string[] } =
      this.getNewPointCustomFields(point);

    const descriptionMentions = getDescriptionMentions(point);
    const pointMentions = [...mentions, ...descriptionMentions];
    const pointAttachments = this.getNewPointAttachments();

    return {
      customFieldsList,
      description: point.description,
      descriptionRich: point.descriptionRich,
      mentions: pointMentions,
      documents: pointAttachments.documents,
      images: pointAttachments.images,
      images360: pointAttachments.images360,
      videos: pointAttachments.videos,
      pins: point.pins,
      polygons: polygons,
      priority: point.priority,
      status: point.status,
      tags: point.tags,
      title: point.title,
      assignees: point.assignees,
    };
  }

  setMapPoints(points: TPoint[]): void {
    this.mapPoints = points;
  }

  getMapPoints(): TPoint[] {
    return this.mapPoints;
  }

  getOverviewSelectedWorkspaces(): string[] {
    const allPoints = this.getAllPoints();
    const workspaces: string[] = [];
    const selectedPointsIds = getSelectedPoints();

    let selectedPoints = [];

    Object.keys(allPoints).forEach((workspaceId) => {
      selectedPoints = [
        ...selectedPoints,
        ...allPoints[workspaceId].entities.filter((point) => selectedPointsIds.includes(point._id)),
      ];
    });

    if (selectedPoints.length > 0) {
      selectedPoints.forEach((point) => {
        if (!workspaces.includes(point.workspaceRef.id)) {
          workspaces.push(point.workspaceRef.id);
        }
      });
    }

    return workspaces;
  }

  getSelectedPointsExport(): string[] {
    const selectedPoints = this.getSelectedPoints();
    const pointIds: string[] = [];

    let points: TPoint[];

    if (selectedPoints.length > 0) {
      points = this.siteTablePointsService.sortPoints(selectedPoints);
    } else {
      const visiblePoints = this.customTableService.getTable().points;

      if (visiblePoints.length > 0) {
        points = this.siteTablePointsService.sortPoints(visiblePoints);
      } else {
        points = this.getPoints();
        points = this.siteTablePointsService.sortPoints(points);
      }
    }

    points.forEach((point) => {
      pointIds.push(point._id);
    });

    return pointIds;
  }

  getNewPointCustomFields(point: TPoint): {
    customFieldsList: TPointCustomField[];
    mentions: string[];
  } {
    let mentions: string[] = [];

    const customFieldsList: TPointCustomField[] = Object.keys(point.customFieldsMap)
      .filter((key) => {
        const pointCustomField = point.customFieldsMap[key];

        if (
          (pointCustomField.value !== undefined && pointCustomField.value !== null) ||
          pointCustomField.subValues?.length > 0 ||
          pointCustomField.selectedItemIds?.length > 0
        ) {
          return true;
        }

        return false;
      })
      .map((key) => {
        const customField = GET_CUSTOM_FIELDS()[key];
        const pointCustomField = point.customFieldsMap[key];

        switch (customField.type) {
          case ECustomFieldType.LIST:
            return {
              customFieldTemplateId: key,
              idOfChosenElement: pointCustomField.idOfChosenElement,
            };
          case ECustomFieldType.MULTI_LIST:
            return {
              customFieldTemplateId: key,
              selectedItemIds: pointCustomField.selectedItemIds,
            };
          case ECustomFieldType.COST:
          case ECustomFieldType.PERCENTAGE:
          case ECustomFieldType.NUMBERS: {
            return {
              ...(!customField.subValuesActive && {
                value: pointCustomField.value
                  ? pointCustomField.value.toString().replace(/,/g, '')
                  : null,
              }),
              customFieldTemplateId: key,
              subValues: pointCustomField.subValues,
            };
          }
          case ECustomFieldType.FORMULA:
            switch (customField.outputType) {
              case ECustomFieldType.COST:
              case ECustomFieldType.PERCENTAGE:
                return {
                  customFieldTemplateId: key,
                  value: pointCustomField.value.replace(/,/g, ''),
                };
            }
          case ECustomFieldType.RICHTEXT:
            mentions = [...mentions, ...pointCustomField.mentions];
            return {
              customFieldTemplateId: key,
              value: pointCustomField.value,
            };

          default:
            return {
              customFieldTemplateId: key,
              value: pointCustomField.value,
            };
        }
      });

    return { customFieldsList, mentions };
  }

  getNewPointAttachments(): TNewPointAttachments {
    const attachments = this.attachmentsService.getAttachments();

    const pointAttachments: TNewPointAttachments = {
      images: [],
      videos: [],
      images360: [],
      documents: [],
    };

    attachments.files.attachmentIds.forEach((attachment) => {
      pointAttachments.documents.push({
        id: attachment,
      });
    });

    Object.keys(attachments.media.attachments).forEach((attachmentId) => {
      const attachment = attachments.media.attachments[attachmentId];

      switch (attachment.type) {
        case EDocumentType.IMAGE:
          pointAttachments.images.push({
            id: attachmentId,
          });
          break;
        case EDocumentType.IMAGE_360:
          pointAttachments.images360.push({
            id: attachmentId,
          });
          break;
        case EDocumentType.VIDEO:
          pointAttachments.videos.push({
            id: attachmentId,
          });
          break;
      }
    });

    return pointAttachments;
  }
}
